/**
 * P22 (clinic-card-polish, Nov 2026): the CRM queue card's sections open the
 * way staff last left them, from patient to patient.
 *
 * What is stored, and nothing else: in sessionStorage (this tab only; gone
 * when the tab closes), one key per login, `crmCardSections.v1:<login id>`,
 * whose value maps a FIXED section id to true (open) or false (closed). No
 * patient, card, referral or visit id, no name and no field value, in the key
 * or the value. A clinic login shared by a front desk shares its choices.
 *
 * Storage blocked, full or throwing: the section's default, silently.
 */
export const CARD_SECTIONS_KEY = "crmCardSections.v1";
export const CARD_SECTION_IDS = Object.freeze(["email", "physician", "address", "case", "icd10"]);

const LOGIN_ID = /^[A-Za-z0-9_-]{1,64}$/;

function keyFor(loginId) {
  const id = loginId === null || loginId === undefined ? "" : String(loginId).trim();
  return LOGIN_ID.test(id) ? `${CARD_SECTIONS_KEY}:${id}` : null;
}

function readMap(key) {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const out = {};
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      for (const id of CARD_SECTION_IDS) {
        if (typeof parsed[id] === "boolean") out[id] = parsed[id];
      }
    }
    return out;
  } catch (e) {
    return null;
  }
}

/** The saved open state for one section and login: true, false, or undefined (none). */
export function savedSectionOpen(loginId, sectionId) {
  if (!CARD_SECTION_IDS.includes(sectionId)) return undefined;
  const key = keyFor(loginId);
  if (!key) return undefined;
  const map = readMap(key);
  return map ? map[sectionId] : undefined;
}

/** Save one section's open state for this login. Only a staff click calls this. */
export function saveSectionOpen(loginId, sectionId, open) {
  if (!CARD_SECTION_IDS.includes(sectionId)) return;
  const key = keyFor(loginId);
  if (!key) return;
  try {
    const map = readMap(key) || {};
    map[sectionId] = open === true;
    window.sessionStorage.setItem(key, JSON.stringify(map));
  } catch (e) {
    // blocked or full: today's behaviour (nothing remembered), no noise
  }
}
